// src/pages/builder/VideoUploadPanel.js
//
// VIDEO UPLOAD STUDIO — Layout Builder panel ("My video").
//
// Upload a video you made yourself and turn it into an SBL lesson video:
//
//   1. YOUR VIDEO   — upload an MP4/MOV/WebM. It plays as the background.
//   2. LYRICS       — the topic's sing-along timings put the lyrics along the
//                     bottom, lighting up word by word, in the same dark bar
//                     as the Lesson video studio. If your video's song starts
//                     a few seconds in, set "Song starts at" (or press
//                     "Lyrics start now" while it plays).
//   3. SHAPES       — draw rectangles, rounded boxes or circles straight on
//                     the preview to cover text that is already in your
//                     video. Fill with a colour (🎯 picks it from the video
//                     so it matches the background) or blur what's under it.
//                     Whole video, or between two times.
//   4. PICTURES     — show an AI picture (or any picture) from one time to
//                     another: "0:12 → 0:18, the water cycle". Make it right
//                     here with the Image studio's generateImage function,
//                     pick one already made, or upload one.
//
// SYNC TEXT (the lyrics) can be switched off for a video that shouldn't
// have it. When it's on it is drawn LAST — on top of every shape and
// picture.
//                     "Anywhere" lets you drag and resize it on the preview.
//   5. MAKE VIDEO   — records an MP4 in the browser, then save it to SBL and
//                     put it on the topic's Video or Sing-along button.
//
// SEPARATE FROM THE LESSON VIDEO STUDIO
// -------------------------------------
// This is its own panel, its own renderer (components/video/
// uploadedVideoFrame.js), its own exporter and its own Firestore collection.
// It reads lyricSync (timings) but never writes it, and never reads or
// writes lessonStoryboards — so nothing here can change how the Lesson
// video studio or the Sing-along studio work.
//
// WHAT IT READS AND WRITES
// ------------------------
// Reads topicLibrary, lyricSync, lyricImages, generatedImages, videoProjects.
// Writes, only when you press a button:
//   * video-studio/{topic}/source-….mp4        — Upload video (admin-only path)
//   * video-studio/{topic}/frames/….jpg        — a frame used as an AI reference
//   * topic-images/video-studio/….png          — Upload picture
//   * videoProjects/{topic}                    — Save (and after an upload)
//   * videos/{topic}{year}myvideo.mp4          — Save to SBL storage
//   * topicLibrary/{id}.videoUrl / singAlongUrl — only the "Use as …" buttons
//
// STYLING
// -------
// Reuses the Sing-along studio's scoped .sa-* classes under .sa-panel, plus
// .uv-* rules in components/video/uploadVideo.css.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, storage } from "../../firebase/firebaseConfig";
import {
  attachSingAlongVideo,
  attachTopicVideo,
  exactKey,
  fileKeyOf,
  getAllLibraryTopics,
  legacyKey,
  makeToolKey,
} from "../../services/topicLibraryService";
import {
  SBL_LOGOS,
  cleanCues,
  formatTime,
  fullSongUrlOf,
  getAllLyricSyncs,
  mediaKey,
  parseLyrics,
  parseTime,
  syncMatchesSong,
  toLyricLines,
  wordsOf,
} from "../../services/lyricSyncService";
import { getAllLyricImages, lyricImageUrls } from "../../services/lessonStoryboardService";
import {
  cleanOverlays,
  cleanShapes,
  getAllVideoProjects,
  projectSignature,
  saveVideoProject,
} from "../../services/videoProjectService";
import { canExportVideo, exportUploadedVideo } from "../../components/video/exportUploadedVideo";
import { pickVideoMime } from "../../components/singalong/exportSingAlongVideo";
import ExportPreview from "../../components/singalong/ExportPreview";
import {
  PICTURE_FITS,
  SHAPE_TYPES,
  clipActiveAt,
  lyricBarAt,
  sampleVideoColor,
  shapeActiveAt,
  onOverlayPictureLoaded,
  preloadOverlayPictures,
  renderUploadedFrame,
} from "../../components/video/uploadedVideoFrame";
import "../../components/singalong/singalong.css";
import "../../components/video/uploadVideo.css";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const BASE_W = 1280;
const BASE_H = 720;

const SIZES = {
  "720": { width: 1280, height: 720, label: "720p (faster upload)" },
  "1080": { width: 1920, height: 1080, label: "1080p (sharper)" },
};

const VIDEO_ACCEPT = "video/*,.mp4,.mov,.webm,.m4v";
const IMAGE_ACCEPT = "image/*,.png,.jpg,.jpeg,.webp";

const AI_STYLES = [
  { key: "sbl", label: "SBL cartoon" },
  { key: "storybook", label: "Storybook" },
  { key: "realistic", label: "Realistic photo" },
  { key: "diagram", label: "Diagram" },
];
const AI_QUALITIES = [
  { key: "standard", label: "Standard" },
  { key: "fast", label: "Fast (cheapest)" },
  { key: "best", label: "Best detail (costs most)" },
];

const round2 = (n) => Math.round(Number(n) * 100) / 100;
const stamp = () => new Date().toISOString().replace(/[-:]/g, "").replace(/\..*$/, "").replace("T", "-");
const extOf = (name, fallback) => ((String(name || "").match(/\.([a-z0-9]+)$/i) || [])[1] || fallback).toLowerCase();
const fileNameOf = (url) => mediaKey(url).split("/").pop() || "";
const vol01 = (v, d = 1) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : d;
};

/** The topic's song as the Topic Library has it now (the full song, not a
 *  sing-along's faded copy). */
const librarySongOf = (topic, sync) => fullSongUrlOf(sync, topic?.musicUrl) || topic?.musicUrl || "";

/** True when the Topic Library's song is no longer the file the lyric
 *  timings were made for (someone changed the song after the sing-along). */
export const songChangedSince = (topic, sync) =>
  Boolean(sync?.musicUrl && topic?.musicUrl && (sync.cues || []).length && !syncMatchesSong(sync, topic.musicUrl));

/**
 * The song file this studio plays and records.
 *
 * Normally the file the lyric timings were made against — the same song as
 * the Topic Library's — so "Song starts at" moves the song and the lyrics
 * together.
 *
 * When the Topic Library's song has been CHANGED since the timings were made,
 * the new song wins (it used to keep playing the old file, which looked like
 * the change hadn't worked). `oldSong` (the project's "Play the old song"
 * choice) keeps the file the timings were made for instead.
 */
export const songUrlFor = (topic, sync, { oldSong = false } = {}) => {
  const current = librarySongOf(topic, sync);
  if (!sync?.musicUrl) return current;
  if (songChangedSince(topic, sync) && !oldSong) return current;
  return sync.musicUrl;
};

/**
 * "Song starts at" box. A plain number box rewrote what you typed on every
 * key — "-" became 0 and "2." became 2 — so a negative or a decimal could
 * never be typed and the shift seemed not to work. This one lets you type
 * freely and applies the number on Enter or when you leave the box.
 */
const OffsetInput = ({ value, onCommit }) => {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = () => {
    const n = Number(String(text).trim().replace(",", "."));
    if (String(text).trim() === "" || !Number.isFinite(n)) setText(String(value));
    else onCommit(round2(n));
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      className="uv-num"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
      }}
      aria-label="Seconds into your video where the song starts"
    />
  );
};

let clipSeq = 0;
const newClipId = () => `c${Date.now().toString(36)}${(clipSeq += 1)}`;

/** motion -> motion2026myvideo.mp4 — its own word, so it never overwrites
 *  the topic's …video.mp4, …singalong.mp4 or …animated.mp4. */
const myVideoFileName = (slug, ext = "mp4") =>
  `${fileKeyOf(slug)}${new Date().getFullYear()}myvideo.${String(ext).replace(/^\./, "")}`;

/** Uploads a Blob to Storage with progress. Resolves { url, path }. */
const uploadTo = (path, blob, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef(storage, path), blob, { contentType });
    task.on(
      "state_changed",
      (snap) => onProgress && onProgress(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      reject,
      async () => {
        try {
          resolve({ url: await getDownloadURL(task.snapshot.ref), path });
        } catch (err) {
          reject(err);
        }
      }
    );
  });

/** Loads the SBL logo so the canvas can still be recorded (needs bucket CORS). */
const useLogo = () => {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => alive && setLogo(img);
    img.src = SBL_LOGOS.white;
    return () => {
      alive = false;
    };
  }, []);
  return logo;
};

/** A time box: shows 1:05.3, accepts 1:05.3 or 65.3, commits on Enter/blur. */
const TimeBox = ({ value, onCommit, disabled, label }) => {
  const [text, setText] = useState(formatTime(value));
  useEffect(() => setText(formatTime(value)), [value]);
  const commit = () => {
    const t = parseTime(text);
    if (t === null || !Number.isFinite(t)) setText(formatTime(value));
    else onCommit(round2(Math.max(0, t)));
  };
  return (
    <input
      className="uv-time"
      value={text}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
          e.currentTarget.blur();
        }
      }}
    />
  );
};

/** The lyric line being sung at a video time, for AI prompts and labels. */
const lyricAtVideoTime = (parsed, cues, offset, t) => {
  const bar = lyricBarAt(parsed, cues, t - (Number(offset) || 0));
  return bar?.text || "";
};

/* ------------------------------------------------------------------ */
/* Choose a picture: this song's lyric pictures + recent AI pictures   */
/* ------------------------------------------------------------------ */

const PicturePicker = ({ pictureMap, lines, fileKey, onPick, onClose }) => {
  const [recent, setRecent] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "generatedImages"), orderBy("createdAt", "desc"), limit(40)));
        if (alive) setRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((r) => r.url));
      } catch (err) {
        if (alive) {
          setRecent([]);
          setError(err?.code === "permission-denied" ? "" : `Couldn’t load recent pictures: ${err?.message || err}`);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const songPics = Object.entries(pictureMap || {})
    .map(([i, url]) => ({ i: Number(i), url, text: lines[Number(i)] || "" }))
    .sort((a, b) => a.i - b.i);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(0.01);
    try {
      const path = `topic-images/video-studio/${fileKey}-${stamp()}.${extOf(file.name, "png")}`;
      const out = await uploadTo(path, file, file.type || "image/png", (f) => setUploading(Math.max(0.01, f)));
      onPick({ url: out.url, path: out.path, prompt: "" });
    } catch (err) {
      setError(`Upload failed: ${err?.message || err}`);
    } finally {
      setUploading(0);
    }
  };

  return (
    <div className="uv-picker-pop" role="dialog" aria-label="Choose a picture" onClick={onClose}>
      <div className="uv-picker" onClick={(e) => e.stopPropagation()}>
        <div className="uv-picker-head">
          <strong>Choose a picture</strong>
          <label className={`lb-btn lb-btn-sm lb-btn-soft uv-upload ${uploading ? "is-disabled" : ""}`}>
            {uploading ? `Uploading ${Math.round(uploading * 100)}%` : "⬆ Upload a picture"}
            <input type="file" accept={IMAGE_ACCEPT} className="uv-file-input" onChange={onFile} disabled={Boolean(uploading)} />
          </label>
          <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
        {error && <div className="sa-banner tone-red">{error}</div>}

        {songPics.length > 0 && (
          <>
            <p className="uv-picker-label">This song’s lyric pictures</p>
            <div className="uv-picker-grid">
              {songPics.map((p) => (
                <button key={`l${p.i}`} type="button" className="uv-thumb" onClick={() => onPick({ url: p.url, path: "", prompt: p.text })}>
                  <img src={p.url} alt="" loading="lazy" />
                  <span>{p.text}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <p className="uv-picker-label">Recent Image studio pictures</p>
        {recent === null ? (
          <p className="sa-muted">Loading…</p>
        ) : recent.length === 0 ? (
          <p className="sa-muted">None yet. Use ✨ Make AI picture on a picture clip, or upload one.</p>
        ) : (
          <div className="uv-picker-grid">
            {recent.map((r) => (
              <button key={r.id} type="button" className="uv-thumb" onClick={() => onPick({ url: r.url, path: r.path || "", prompt: r.prompt || "" })}>
                <img src={r.url} alt="" loading="lazy" />
                <span>{r.prompt}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sync text on / off                                                  */
/* ------------------------------------------------------------------ */

const SyncToggle = ({ on, onChange, disabled, compact = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    className={`uv-sync ${on ? "is-on" : ""} ${compact ? "is-compact" : ""}`}
    onClick={() => onChange(!on)}
    disabled={disabled}
    title={on ? "Sync text is ON — the lyrics show along the bottom" : "Sync text is OFF — no lyrics on this video"}
  >
    <span className="uv-sync-track" aria-hidden="true">
      <span className="uv-sync-knob" />
    </span>
    Sync text {on ? "ON" : "OFF"}
  </button>
);

/* ------------------------------------------------------------------ */
/* One shape (cover-up)                                                */
/* ------------------------------------------------------------------ */

const ShapeRow = ({ shape, n, now, selected, picking, onSelect, onChange, onRemove, onDuplicate, onPick }) => {
  const set = (patch) => onChange({ ...shape, ...patch });
  const showing = shapeActiveAt(shape, now);
  return (
    <div className={`uv-shape ${selected ? "is-selected" : ""} ${showing ? "" : "is-hidden-now"}`} onClick={onSelect}>
      <span
        className={`uv-shape-swatch uv-kind-${shape.kind} ${shape.mode === "blur" ? "is-blur" : ""}`}
        style={shape.mode === "blur" ? undefined : { background: shape.fill, opacity: Math.max(0.25, shape.opacity) }}
        aria-hidden="true"
      >
        <b>{n}</b>
      </span>

      <div className="uv-shape-body" onClick={(e) => e.stopPropagation()}>
        <div className="uv-shape-line">
          <label>
            Shape
            <select value={shape.kind} onChange={(e) => set({ kind: e.target.value })}>
              {SHAPE_TYPES.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.icon} {k.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fill
            <select value={shape.mode} onChange={(e) => set({ mode: e.target.value })}>
              <option value="color">Solid colour</option>
              <option value="blur">Blur the video under it</option>
            </select>
          </label>
          {shape.mode === "color" && (
            <>
              <label>
                Colour
                <span className="uv-color">
                  <input type="color" value={shape.fill} onChange={(e) => set({ fill: e.target.value })} aria-label={`Shape ${n} colour`} />
                  <code>{shape.fill}</code>
                </span>
              </label>
              <button
                type="button"
                className={`lb-btn lb-btn-sm ${picking ? "" : "lb-btn-soft"} uv-pick-btn`}
                onClick={onPick}
                title="Then click your video on the preview to copy the colour there"
              >
                🎯 {picking ? "Click the preview…" : "Pick from video"}
              </button>
              <label className="uv-opacity">
                See-through
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round((1 - shape.opacity) * 100)}
                  onChange={(e) => set({ opacity: 1 - Number(e.target.value) / 100 })}
                  aria-label={`Shape ${n} see-through`}
                />
              </label>
            </>
          )}
        </div>

        <div className="uv-shape-line">
          <label className="sa-check">
            <input type="checkbox" checked={shape.always} onChange={(e) => set({ always: e.target.checked, from: shape.from || 0, to: shape.to > shape.from ? shape.to : round2(now + 5) })} />
            Whole video
          </label>
          {!shape.always && (
            <div className="uv-clip-times">
              <label>
                From
                <TimeBox value={shape.from} label={`Shape ${n} starts`} onCommit={(t) => set({ from: t, to: Math.max(t + 0.5, shape.to) })} />
                <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => set({ from: round2(now), to: Math.max(round2(now) + 0.5, shape.to) })}>
                  ⟵ now
                </button>
              </label>
              <label>
                To
                <TimeBox value={shape.to} label={`Shape ${n} ends`} onCommit={(t) => set({ to: Math.max(shape.from + 0.5, t) })} />
                <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => set({ to: Math.max(shape.from + 0.5, round2(now)) })}>
                  ⟵ now
                </button>
              </label>
            </div>
          )}
          <span className="uv-shape-actions">
            <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={onSelect}>
              {selected ? "✓ Selected" : "Select"}
            </button>
            <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={onDuplicate}>
              Duplicate
            </button>
            <button type="button" className="lb-btn lb-btn-sm lb-btn-danger" onClick={onRemove}>
              ✕
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* One picture clip                                                    */
/* ------------------------------------------------------------------ */

const ClipRow = ({ clip, n, now, duration, lyricAt, selected, onSelect, onChange, onRemove, onPreview, onChoose, makeAi }) => {
  const [aiOpen, setAiOpen] = useState(!clip.url);
  const [idea, setIdea] = useState(clip.prompt || "");
  const [style, setStyle] = useState("sbl");
  const [quality, setQuality] = useState("standard");
  const [useFrame, setUseFrame] = useState(false);
  const [useLyric, setUseLyric] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // a picture just arrived (made, chosen or uploaded): fold the AI box away
  useEffect(() => {
    if (clip.url) setAiOpen(false);
  }, [clip.url]);

  const set = (patch) => onChange({ ...clip, ...patch });
  const lyric = lyricAt(clip.from);
  const len = Math.max(0, clip.to - clip.from);
  const tooLong = duration > 0 && clip.from >= duration;

  const make = async () => {
    setError("");
    setBusy(true);
    try {
      const out = await makeAi({ clip, idea, style, quality, useFrame, lyric: useLyric ? lyric : "" });
      set({ url: out.url, path: out.path, prompt: idea || lyric });
      setAiOpen(false);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`uv-clip ${now >= clip.from && now < clip.to ? "is-now" : ""} ${selected ? "is-selected" : ""}`}>
      <div className="uv-clip-thumb">
        {clip.url ? <img src={clip.url} alt="" /> : <span>No picture yet</span>}
        <b className="uv-clip-n">{n}</b>
      </div>

      <div className="uv-clip-body">
        <div className="uv-clip-times">
          <label>
            From
            <TimeBox value={clip.from} label={`Picture ${n} starts`} onCommit={(t) => set({ from: t, to: Math.max(t + 0.5, clip.to) })} />
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" title="Start at the current time" onClick={() => set({ from: round2(now), to: Math.max(round2(now) + 0.5, clip.to) })}>
              ⟵ now
            </button>
          </label>
          <label>
            To
            <TimeBox value={clip.to} label={`Picture ${n} ends`} onCommit={(t) => set({ to: Math.max(clip.from + 0.5, t) })} />
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" title="End at the current time" onClick={() => set({ to: Math.max(clip.from + 0.5, round2(now)) })}>
              ⟵ now
            </button>
          </label>
          <span className="sa-muted uv-clip-len">{len.toFixed(1)}s</span>
        </div>

        <div className="uv-clip-opts">
          <label>
            Layout
            <select
              value={clip.fit}
              onChange={(e) => {
                const fit = e.target.value;
                // a fresh "Anywhere" picture starts as a box in the middle
                const box = fit === "custom" && !(clip.w > 0 && clip.h > 0) ? { x: 0.3, y: 0.15, w: 0.4, h: 0.45 } : {};
                set({ fit, ...box });
                if (fit === "custom") onSelect();
              }}
            >
              {PICTURE_FITS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="uv-grow">
            Words over the picture
            <input
              value={clip.caption}
              placeholder="Optional — e.g. 3 × 4 = 12"
              onChange={(e) => set({ caption: e.target.value })}
            />
          </label>
          <label className="sa-check">
            <input type="checkbox" checked={!clip.still} onChange={(e) => set({ still: !e.target.checked })} />
            Slow zoom
          </label>
          {clip.fit === "custom" && (
            <label className="sa-check">
              <input type="checkbox" checked={Boolean(clip.frame)} onChange={(e) => set({ frame: e.target.checked })} />
              White frame
            </label>
          )}
        </div>
        {clip.fit === "custom" && (
          <p className="sa-muted uv-clip-lyric">
            {selected
              ? "Selected — drag it on the preview to move it, drag a corner to resize."
              : "Press “Place on preview”, then drag it where you want it."}
          </p>
        )}

        {lyric && (
          <p className="sa-muted uv-clip-lyric">
            Sung at {formatTime(clip.from)}: “{lyric}”
          </p>
        )}
        {tooLong && <p className="uv-warn">This starts after your video ends, so it will never show.</p>}

        <div className="uv-clip-actions">
          <button type="button" className="lb-btn lb-btn-sm lb-btn-soft" onClick={() => setAiOpen((v) => !v)}>
            ✨ {clip.url ? "Make a new AI picture" : "Make AI picture"}
          </button>
          <button type="button" className="lb-btn lb-btn-sm lb-btn-soft" onClick={() => onChoose(clip)}>
            🖼 Choose / upload picture
          </button>
          <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={() => onPreview(clip)}>
            ▶ Preview
          </button>
          {clip.fit === "custom" && (
            <button type="button" className="lb-btn lb-btn-sm lb-btn-ghost" onClick={onSelect}>
              {selected ? "✓ Selected" : "Place on preview"}
            </button>
          )}
          <button type="button" className="lb-btn lb-btn-sm lb-btn-danger" onClick={onRemove}>
            ✕ Remove
          </button>
        </div>

        {aiOpen && (
          <div className="uv-ai">
            <label className="uv-grow">
              Describe the picture
              <textarea
                rows={2}
                value={idea}
                placeholder={lyric ? `e.g. ${lyric}` : "e.g. A bright water cycle: sun, clouds, rain and a river"}
                onChange={(e) => setIdea(e.target.value)}
              />
            </label>
            <div className="uv-ai-row">
              <label>
                Style
                <select value={style} onChange={(e) => setStyle(e.target.value)}>
                  {AI_STYLES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Quality
                <select value={quality} onChange={(e) => setQuality(e.target.value)}>
                  {AI_QUALITIES.map((q) => (
                    <option key={q.key} value={q.key}>
                      {q.label}
                    </option>
                  ))}
                </select>
              </label>
              {lyric && (
                <label className="sa-check">
                  <input type="checkbox" checked={useLyric} onChange={(e) => setUseLyric(e.target.checked)} />
                  Use the lyric sung here
                </label>
              )}
              <label className="sa-check" title="Sends the video frame at the From time to the picture maker as a reference">
                <input type="checkbox" checked={useFrame} onChange={(e) => setUseFrame(e.target.checked)} />
                Match my video frame
              </label>
              <button type="button" className="lb-btn lb-btn-sm" disabled={busy || (!idea.trim() && !(useLyric && lyric))} onClick={make}>
                {busy ? "Making… (up to a minute)" : "✨ Make it"}
              </button>
            </div>
            {error && <div className="sa-banner tone-red">{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* MP4 export                                                          */
/* ------------------------------------------------------------------ */

const ExportBox = ({ topic, slug, project, sync, dirty, localFile, renderFrame, overlayUrls, onProjectSaved, onTopicChanged }) => {
  const [size, setSize] = useState("720");
  const [audible, setAudible] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | done | uploading | saved | error
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);

  useEffect(
    () => () => {
      if (result?.url) URL.revokeObjectURL(result.url);
      abortRef.current?.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const supported = canExportVideo();
  const mime = pickVideoMime();
  const willBeMp4 = Boolean(mime && mime.startsWith("video/mp4"));
  // A video recorded with the OLD song must show as out of date once the
  // new Topic Library song is chosen. Only added in that case, so every video
  // made before this keeps its "up to date" status.
  const usesNewSong = Boolean(project?.songOn && songChangedSince(topic, sync) && !project?.oldSong);
  const sig = projectSignature(usesNewSong ? { ...project, songFile: mediaKey(librarySongOf(topic, sync)) } : project, sync);
  const saved = project?.outputUrl;
  const savedCurrent = saved && project.outputSig === sig;
  const onVideo = topic.videoUrl && saved && mediaKey(topic.videoUrl) === mediaKey(saved);
  const onSingAlong = topic.singAlongUrl && saved && mediaKey(topic.singAlongUrl) === mediaKey(saved);
  const hasSource = Boolean(localFile || project?.sourceUrl);

  const make = async () => {
    setError(null);
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
    setProgress(0);
    setPhase("recording");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (overlayUrls.length) {
        setStatus(`Loading ${overlayUrls.length} picture${overlayUrls.length === 1 ? "" : "s"}…`);
        const pre = await preloadOverlayPictures(overlayUrls);
        if (!pre.ok) {
          throw Object.assign(
            new Error(
              `${pre.failed.length} picture${pre.failed.length === 1 ? "" : "s"} couldn’t be loaded for recording, so ${
                pre.failed.length === 1 ? "it" : "they"
              } would come out blank. Choose ${pre.failed.length === 1 ? "it" : "them"} again, or check the bucket’s CORS lists ${window.location.origin}.`
            ),
            { code: "pictures" }
          );
        }
        setStatus("");
      }
      const out = await exportUploadedVideo({
        source: localFile || null,
        sourceUrl: project.sourceUrl,
        width: SIZES[size].width,
        height: SIZES[size].height,
        audible,
        logoUrl: project.showLogo !== false ? SBL_LOGOS.white : null,
        songUrl: project.songOn ? songUrlFor(topic, sync, { oldSong: Boolean(project.oldSong) }) : "",
        songOffset: Number(project.offset) || 0,
        songVolume: vol01(project.songVolume, 1),
        videoVolume: vol01(project.videoVolume, 1),
        renderFrame: (ctx, { width, height, t, video, logo }) => renderFrame(ctx, { W: width, H: height, t, video, logo }),
        signal: controller.signal,
        onProgress: (f) => setProgress(f),
        onStatus: setStatus,
      });
      setResult({ ...out, url: URL.createObjectURL(out.blob), sig });
      setPhase("done");
    } catch (err) {
      if (err?.code === "aborted") setPhase("idle");
      else {
        setError({ code: err?.code || "error", message: err?.message || String(err) });
        setPhase("error");
      }
    } finally {
      abortRef.current = null;
      setStatus("");
    }
  };

  const upload = async () => {
    if (!result) return;
    setPhase("uploading");
    setProgress(0);
    try {
      const path = `videos/${myVideoFileName(slug, result.ext)}`;
      const out = await uploadTo(path, result.blob, result.mime, setProgress);
      const savedDoc = await saveVideoProject(slug, {
        outputUrl: out.url,
        outputPath: path,
        outputSig: result.sig,
        outputMadeAt: new Date().toISOString(),
      });
      onProjectSaved(savedDoc);
      // a button already playing an older copy follows the new file
      if (onVideo) {
        await attachTopicVideo(topic.id || slug, out.url);
        onTopicChanged({ videoUrl: out.url });
      }
      if (onSingAlong) {
        await attachSingAlongVideo(topic.id || slug, out.url);
        onTopicChanged({ singAlongUrl: out.url });
      }
      setPhase("saved");
    } catch (err) {
      setError({ code: "upload", message: err?.message || String(err) });
      setPhase("error");
    }
  };

  const putOn = async (which) => {
    if (!saved) return;
    const field = which === "video" ? "videoUrl" : "singAlongUrl";
    const current = topic[field] ? fileNameOf(topic[field]) : "";
    const button = which === "video" ? "Video" : "🎤 Sing-along";
    if (
      current &&
      !window.confirm(
        `Replace ${current} on this topic's ${button} button with your video? The old file stays in storage, and you can switch back in the Topic Library.`
      )
    )
      return;
    setBusy(true);
    try {
      if (which === "video") await attachTopicVideo(topic.id || slug, saved);
      else await attachSingAlongVideo(topic.id || slug, saved);
      onTopicChanged({ [field]: saved });
    } catch (err) {
      setError({ code: "attach", message: `Could not update the topic: ${err?.message || err}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sa-export uv-step">
      <div className="sa-export-head">
        <strong>
          <span className="uv-step-num">5</span> Make the video
        </strong>
        <span className="sa-muted">
          Records exactly what the preview shows, with your video’s sound, as a{" "}
          {willBeMp4 ? "MP4" : "WebM (use current Chrome or Edge for MP4)"} file. It records in real time, so keep this
          tab open and on screen until it finishes.
        </span>
      </div>

      {saved && (
        <div className="sa-export-current">
          Saved video:{" "}
          <a href={saved} target="_blank" rel="noreferrer">
            {project.outputPath || "open"} ↗
          </a>{" "}
          <span className={`sa-pill-inline tone-${savedCurrent ? "green" : "amber"}`}>
            {savedCurrent ? "up to date" : "out of date — changed since it was made"}
          </span>
        </div>
      )}

      <div className="sa-export-current">
        Video button:{" "}
        {onVideo ? (
          <span className="sa-pill-inline tone-green">plays your video</span>
        ) : (
          <>
            <span className="sa-pill-inline tone-grey">{topic.videoUrl ? `plays ${fileNameOf(topic.videoUrl)}` : "no video"}</span>
            {saved && (
              <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" disabled={busy} onClick={() => putOn("video")}>
                Use as this topic’s video
              </button>
            )}
          </>
        )}
      </div>
      <div className="sa-export-current">
        🎤 Sing-along button:{" "}
        {onSingAlong ? (
          <span className="sa-pill-inline tone-green">plays your video</span>
        ) : (
          <>
            <span className="sa-pill-inline tone-grey">
              {topic.singAlongUrl ? `plays ${fileNameOf(topic.singAlongUrl)}` : "no sing-along video"}
            </span>
            {saved && (
              <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" disabled={busy} onClick={() => putOn("singalong")}>
                Use as the sing-along video
              </button>
            )}
          </>
        )}
      </div>

      {!supported ? (
        <div className="sa-banner tone-amber">
          This browser can’t record video. Open the Layout Builder in the latest Chrome or Edge on a computer.
        </div>
      ) : (
        <>
          <div className="sa-export-row">
            <label className="sa-inline-field">
              Size
              <select value={size} onChange={(e) => setSize(e.target.value)} disabled={phase === "recording"}>
                {Object.entries(SIZES).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-check">
              <input type="checkbox" checked={audible} onChange={(e) => setAudible(e.target.checked)} disabled={phase === "recording"} />
              Play it out loud while recording
            </label>
            {phase === "recording" ? (
              <button type="button" className="lb-btn lb-btn-danger" onClick={() => abortRef.current?.abort()}>
                ■ Cancel
              </button>
            ) : (
              <button
                type="button"
                className="lb-btn"
                onClick={make}
                disabled={phase === "uploading" || dirty || !hasSource}
                title={!hasSource ? "Upload your video first" : dirty ? "Save first" : ""}
              >
                🎬 Make video
              </button>
            )}
          </div>
          {dirty && hasSource && <p className="sa-muted">Press Save first — the video is made from the saved version.</p>}

          {(phase === "recording" || phase === "uploading") && (
            <div className="sa-progress">
              <div className="sa-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
              <span>
                {phase === "recording" ? "Recording" : "Uploading"} {Math.round(progress * 100)}%
                {phase === "recording" ? " — keep this tab visible" : ""}
              </span>
            </div>
          )}
          {status && phase === "recording" && <p className="sa-muted">{status}</p>}

          {error && (
            <div className="sa-banner tone-red">
              {error.code === "cors"
                ? "The browser wasn’t allowed to download your video for recording. The storage bucket’s CORS setting has to list this site (the same one-time fix as the Sing-along studio)."
                : error.message}
            </div>
          )}

          {result && ["done", "uploading", "saved"].includes(phase) && (
            <div className="sa-export-result">
              <ExportPreview url={result.url} label={result.codecLabel} />
              {result.logoMissing && (
                <div className="sa-banner tone-amber" style={{ marginTop: 10 }}>
                  Made <strong>without the SBL logo</strong>: the browser wasn’t allowed to load the logo file.
                </div>
              )}
              <div className="sa-export-actions">
                <a className="lb-btn lb-btn-soft" href={result.url} download={`${slug}-myvideo.${result.ext}`}>
                  ⬇ Download {result.ext.toUpperCase()}
                </a>
                <button type="button" className="lb-btn" onClick={upload} disabled={phase === "uploading" || phase === "saved"}>
                  {phase === "saved" ? "✓ Saved to SBL" : "☁ Save to SBL storage"}
                </button>
              </div>
              {phase === "saved" && (
                <p className="sa-muted">
                  Saved as <code>videos/{myVideoFileName(slug, result.ext)}</code>. Use the buttons above to put it on
                  this topic’s Video or Sing-along button.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Studio: one topic                                                   */
/* ------------------------------------------------------------------ */

const Studio = ({ topic, sync, project: savedProject, pictureMap, onClose, onProjectSaved, onTopicChanged }) => {
  const slug = topic.slug;
  const fileKey = fileKeyOf(slug);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const uiAtRef = useRef(0);
  const logo = useLogo();

  // lyrics + timings: the sing-along's, else the topic's lyrics with no timings
  const lines = useMemo(
    () => (sync?.lines?.length ? toLyricLines(sync.lines) : toLyricLines(topic.lyrics)),
    [sync, topic.lyrics]
  );
  const parsed = useMemo(() => parseLyrics(lines), [lines]);
  const cues = useMemo(() => cleanCues(sync?.cues, parsed), [sync, parsed]);
  const wordTimedCount = useMemo(
    () =>
      parsed.sung.filter((i) => {
        const n = wordsOf(parsed.lines[i]).length;
        return n > 0 && cues.some((c) => c.i === i && Array.isArray(c.w) && c.w.length === n);
      }).length,
    [parsed, cues]
  );

  const [proj, setProjState] = useState(() => ({
    sourceUrl: savedProject?.sourceUrl || "",
    sourcePath: savedProject?.sourcePath || "",
    sourceName: savedProject?.sourceName || "",
    duration: savedProject?.duration || 0,
    offset: Number(savedProject?.offset) || 0,
    // the topic's song played with the video (for a video without the song,
    // or with a different recording of it), and the two volumes
    songOn: Boolean(savedProject?.songOn),
    // the Topic Library's song changed since the lyric timings were made:
    // true = keep playing the song the timings were made for
    oldSong: Boolean(savedProject?.oldSong),
    songVolume: vol01(savedProject?.songVolume, 1),
    videoVolume: vol01(savedProject?.videoVolume, 1),
    showLyrics: savedProject?.showLyrics !== false,
    showLogo: savedProject?.showLogo !== false,
    overlays: cleanOverlays(savedProject?.overlays),
    shapes: cleanShapes(savedProject?.shapes),
    outputUrl: savedProject?.outputUrl || "",
    outputPath: savedProject?.outputPath || "",
    outputSig: savedProject?.outputSig || "",
  }));
  const projRef = useRef(proj);
  projRef.current = proj;
  const [dirty, setDirty] = useState(false);
  const setProj = (patch) => {
    setProjState((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }));
    setDirty(true);
  };

  const [localFile, setLocalFile] = useState(null);
  const [localUrl, setLocalUrl] = useState("");
  useEffect(() => () => localUrl && URL.revokeObjectURL(localUrl), [localUrl]);
  const videoSrc = localUrl || proj.sourceUrl;

  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(proj.duration || 0);
  const [playing, setPlaying] = useState(false);
  const [videoError, setVideoError] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [picking, setPicking] = useState(null); // clip id being chosen for
  const flash = (text, tone = "info") => setNotice({ text, tone, at: Date.now() });

  /* ---------- drawing ---------- */

  const renderFrame = useCallback(
    (ctx, { W, H, t, video, logo: lg }) =>
      renderUploadedFrame(ctx, {
        W,
        H,
        t,
        video,
        offset: projRef.current.offset,
        parsed,
        cues,
        showLyrics: projRef.current.showLyrics,
        overlays: projRef.current.overlays,
        shapes: projRef.current.shapes,
        logo: projRef.current.showLogo ? lg : null,
      }),
    [parsed, cues]
  );

  const draw = useCallback(
    (t) => {
      const c = canvasRef.current;
      if (!c) return;
      renderFrame(c.getContext("2d"), { W: c.width, H: c.height, t, video: videoRef.current, logo });
    },
    [renderFrame, logo]
  );

  const tick = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const now = v.currentTime;
    if (Math.abs(now - uiAtRef.current) > 0.15 || v.paused) {
      uiAtRef.current = now;
      setTime(now);
    }
    draw(now);
    syncSongRef.current();
    if (!v.paused && !v.ended) rafRef.current = requestAnimationFrame(() => tickRef.current());
  }, [draw]);
  const tickRef = useRef(tick);
  tickRef.current = tick;

  /* ---------- the topic's song, locked to the video ---------- */
  // song time = video time − offset, the same rule the lyrics use, so moving
  // "Song starts at" moves the song and the lyrics together.
  const songChanged = songChangedSince(topic, sync);
  const songUrl = songUrlFor(topic, sync, { oldSong: proj.oldSong });
  const songRef = useRef(null);
  const syncSong = useCallback((force = false) => {
    const a = songRef.current;
    const v = videoRef.current;
    if (!a) return;
    const p = projRef.current;
    if (!p.songOn || !v) {
      if (!a.paused) a.pause();
      return;
    }
    a.volume = vol01(p.songVolume, 1);
    const st = v.currentTime - (Number(p.offset) || 0);
    const dur = Number.isFinite(a.duration) && a.duration > 0 ? a.duration : Infinity;
    const inSong = st >= 0 && st < dur;
    if (v.paused || !inSong) {
      if (!a.paused) a.pause();
      if (force || v.paused) {
        try {
          a.currentTime = inSong ? st : 0;
        } catch {
          /* not loaded yet */
        }
      }
      return;
    }
    if (force || Math.abs(a.currentTime - st) > 0.25) {
      try {
        a.currentTime = st;
      } catch {
        /* not loaded yet */
      }
    }
    if (a.paused) a.play().catch(() => {});
  }, []);
  const syncSongRef = useRef(syncSong);
  syncSongRef.current = syncSong;
  // a shift, the song switched on/off or its volume: line it up again now
  useEffect(() => {
    syncSong(true);
  }, [syncSong, proj.offset, proj.songOn, proj.songVolume, songUrl]);
  // your video's own sound
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = vol01(proj.videoVolume, 1);
    v.muted = vol01(proj.videoVolume, 1) === 0;
  }, [proj.videoVolume, videoSrc]);
  useEffect(
    () => () => {
      const a = songRef.current;
      if (a) a.pause();
    },
    []
  );

  // redraw a paused preview whenever anything that shows changes
  useEffect(() => {
    if (!videoRef.current || videoRef.current.paused) draw(videoRef.current?.currentTime || 0);
  }, [draw, proj]);
  useEffect(
    () =>
      onOverlayPictureLoaded(() => {
        const v = videoRef.current;
        if (!v || v.paused) draw(v?.currentTime || 0);
      }),
    [draw]
  );
  useEffect(() => {
    preloadOverlayPictures(proj.overlays.map((o) => o.url));
  }, [proj.overlays]);
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;
    if (!v.paused) {
      v.pause();
      return;
    }
    if (v.ended) v.currentTime = 0;
    try {
      await v.play();
    } catch {
      setVideoError("Your video would not play in this browser.");
    }
  };

  const seek = (t) => {
    const v = videoRef.current;
    if (!v) return;
    const max = Number.isFinite(v.duration) ? v.duration : t;
    v.currentTime = Math.max(0, Math.min(max, t));
    setTime(v.currentTime);
  };

  /* ---------- 1. upload your video ---------- */

  const onVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (proj.sourceUrl && !window.confirm("Replace the video in this project with the new file? Your pictures and settings stay.")) return;
    setVideoError("");
    const url = URL.createObjectURL(file);
    setLocalFile(file);
    setLocalUrl(url);
    setUploadPct(0.01);
    try {
      const path = `video-studio/${fileKey}/source-${stamp()}.${extOf(file.name, "mp4")}`;
      const out = await uploadTo(path, file, file.type || "video/mp4", (f) => setUploadPct(Math.max(0.01, f)));
      const patch = { sourceUrl: out.url, sourcePath: out.path, sourceName: file.name };
      // saved straight away, so a big upload is never lost to a forgotten Save
      const savedDoc = await saveVideoProject(slug, { ...patch, title: topic.title || slug, topicId: topic.id || slug });
      setProjState((prev) => ({ ...prev, ...patch }));
      onProjectSaved(savedDoc);
      flash(`Uploaded ${file.name}. It’s saved to this project.`, "ok");
    } catch (err) {
      flash(
        err?.code === "storage/unauthorized"
          ? "Storage refused the upload. Only admin accounts can upload here."
          : `Upload failed: ${err?.message || err}. You can still preview it here, but it isn’t saved.`,
        "error"
      );
    } finally {
      setUploadPct(0);
    }
  };

  /* ---------- 2. lyrics ---------- */

  const firstCue = cues[0] || null;
  const lyricsStartNow = () => {
    if (!firstCue) return;
    const v = videoRef.current;
    const t = v ? v.currentTime : time;
    setProj({ offset: round2(t - firstCue.t) });
    flash(
      `The first line now starts at ${formatTime(t)} in your video${projRef.current.songOn ? " — the song moved with it" : ""}. Press Save to keep it.`,
      "ok"
    );
  };

  /* ---------- 3. pictures ---------- */

  const addClip = () => {
    const from = round2(time);
    const clip = { id: newClipId(), from, to: round2(from + 4), url: "", path: "", fit: "board", caption: "", prompt: "", still: false, x: 0.3, y: 0.15, w: 0.4, h: 0.45, frame: false };
    setProj((prev) => ({ overlays: [...prev.overlays, clip].sort((a, b) => a.from - b.from) }));
  };
  const updateClip = (id, next) =>
    setProj((prev) => ({ overlays: prev.overlays.map((o) => (o.id === id ? next : o)) }));
  const removeClip = (id) => {
    if (!window.confirm("Remove this picture from the video?")) return;
    setProj((prev) => ({ overlays: prev.overlays.filter((o) => o.id !== id) }));
  };
  const previewClip = (clip) => {
    seek(Math.max(0, clip.from - 1));
    const v = videoRef.current;
    if (v && v.paused) v.play().catch(() => {});
  };

  const lyricAt = useCallback((t) => lyricAtVideoTime(parsed, cues, proj.offset, t), [parsed, cues, proj.offset]);

  /** Grabs the video frame at time t as a JPEG in Storage; resolves its path. */
  const captureFrame = async (t) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) throw new Error("Load your video first.");
    v.pause();
    await new Promise((resolve) => {
      if (Math.abs(v.currentTime - t) < 0.02) return resolve();
      v.addEventListener("seeked", () => resolve(), { once: true });
      v.currentTime = t;
    });
    const scale = Math.min(1, 1280 / v.videoWidth);
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    let blob;
    try {
      blob = await new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("empty"))), "image/jpeg", 0.88));
    } catch {
      throw new Error("Couldn’t copy the video frame (the bucket’s CORS must list this site). Untick “Match my video frame” and try again.");
    }
    const out = await uploadTo(`video-studio/${fileKey}/frames/frame-${stamp()}.jpg`, blob, "image/jpeg");
    draw(t);
    return out.path;
  };

  const makeAi = async ({ clip, idea, style, quality, useFrame, lyric }) => {
    const refPath = useFrame ? await captureFrame(clip.from) : "";
    const prompt = [
      idea.trim() || lyric,
      `This picture appears in an educational music video about "${topic.title || slug}" for K-8 students.`,
      lyric && idea.trim() ? `The lyric sung at that moment is: "${lyric}".` : "",
      clip.fit === "left" || clip.fit === "right" ? "Keep the main subject centred so it still reads when cropped to a tall half of the screen." : "",
      refPath ? "Match the setting, colours and look of the attached video frame." : "",
    ]
      .filter(Boolean)
      .join("\n\n")
      .slice(0, 1990);
    const call = httpsCallable(functions, "generateImage", { timeout: 170000 });
    try {
      const res = await call({
        prompt,
        style,
        model: quality,
        aspectRatio: "16:9",
        name: `${fileKey}-myvideo-${Math.round(clip.from)}s`,
        refs: refPath ? [refPath] : [],
      });
      const { url, path } = res.data || {};
      if (!url) throw new Error("No picture came back. Try describing it differently.");
      return { url, path };
    } catch (err) {
      const code = String(err?.code || "");
      if (code.includes("permission-denied")) throw new Error("Only SBL admin accounts can make pictures.");
      if (code.includes("deadline-exceeded")) throw new Error("The picture took too long. Press the button again.");
      if (code.includes("not-found")) throw new Error("The picture maker (generateImage) isn’t deployed. See the Image studio setup.");
      throw new Error(err?.message || String(err));
    }
  };

  /* ---------- shapes + placing things on the preview ---------- */
  //
  // An HTML layer sits exactly over the preview canvas. It shows a box for
  // every shape (and every "Anywhere" picture) on screen right now, and
  // turns mouse drags into changes:
  //   * Draw mode (a shape button pressed): drag to draw a new shape.
  //   * Pick mode (🎯 on a shape): click to copy the video's colour there.
  //   * Otherwise: click a box to select it, drag to move, drag a corner
  //     to resize. Delete removes the selected shape; Esc cancels.
  // Everything is stored as fractions of the frame, so it lands in the same
  // place at 720p and 1080p.

  const [selected, setSelected] = useState(null); // { type: "shape" | "pic", id }
  const [drawKind, setDrawKind] = useState(null);
  const [pickFor, setPickFor] = useState(null); // shape id
  const [newColor, setNewColor] = useState("#ffffff");
  const editRef = useRef(null);
  const dragRef = useRef(null);
  const clamp = (v, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));
  const r4 = (v) => Math.round(v * 10000) / 10000;

  const updateShape = (id, next) =>
    setProj((prev) => ({ shapes: prev.shapes.map((x) => (x.id === id ? { ...x, ...next } : x)) }));
  const removeShape = (id) => {
    setProj((prev) => ({ shapes: prev.shapes.filter((x) => x.id !== id) }));
    setSelected((sel) => (sel?.id === id ? null : sel));
  };
  const duplicateShape = (shape) => {
    const copy = { ...shape, id: newClipId(), x: clamp(shape.x + 0.03, 0, 1 - shape.w), y: clamp(shape.y + 0.03, 0, 1 - shape.h) };
    setProj((prev) => ({ shapes: [...prev.shapes, copy] }));
    setSelected({ type: "shape", id: copy.id });
  };
  const patchItem = (sel, patch) => {
    if (!sel) return;
    if (sel.type === "shape") updateShape(sel.id, patch);
    else setProj((prev) => ({ overlays: prev.overlays.map((o) => (o.id === sel.id ? { ...o, ...patch } : o)) }));
  };

  const selectShape = (shape) => {
    setSelected({ type: "shape", id: shape.id });
    if (!shapeActiveAt(shape, time)) seek(shape.from);
  };
  const selectClip = (clip) => {
    setSelected({ type: "pic", id: clip.id });
    if (!clipActiveAt(clip, time)) seek(clip.from + 0.05);
  };

  // boxes to show on the preview: on screen now, plus whatever is selected
  const boxes = [
    ...proj.shapes
      .filter((x) => shapeActiveAt(x, time) || selected?.id === x.id)
      .map((x) => ({ type: "shape", id: x.id, kind: x.kind, x: x.x, y: x.y, w: x.w, h: x.h, n: proj.shapes.indexOf(x) + 1 })),
    ...proj.overlays
      .filter((o) => o.fit === "custom" && (clipActiveAt(o, time) || selected?.id === o.id))
      .map((o) => ({ type: "pic", id: o.id, kind: "rect", x: o.x, y: o.y, w: o.w, h: o.h, n: proj.overlays.indexOf(o) + 1 })),
  ];

  const ptOf = (e) => {
    const r = editRef.current.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };

  const onEditDown = (e) => {
    if (!editRef.current || e.button > 0) return;
    const pt = ptOf(e);
    if (pickFor) {
      try {
        const fill = sampleVideoColor(videoRef.current, pt.x, pt.y);
        updateShape(pickFor, { fill, mode: "color" });
        setNewColor(fill);
        flash(`Colour ${fill} copied from your video.`, "ok");
      } catch (err) {
        flash(
          /secur|taint|cross/i.test(String(err?.message || err))
            ? "The browser won’t let the page read this video’s colours (the bucket’s CORS must list this site). Re-upload the video in this session, or pick the colour by hand."
            : String(err?.message || err),
          "error"
        );
      }
      setPickFor(null);
      return;
    }
    editRef.current.setPointerCapture?.(e.pointerId);
    if (drawKind) {
      const shape = {
        id: newClipId(),
        kind: drawKind,
        x: r4(pt.x),
        y: r4(pt.y),
        w: 0,
        h: 0,
        fill: newColor,
        opacity: 1,
        mode: "color",
        always: true,
        from: round2(time),
        to: round2(time + 5),
      };
      setProj((prev) => ({ shapes: [...prev.shapes, shape] }));
      setSelected({ type: "shape", id: shape.id });
      dragRef.current = { mode: "draw", sel: { type: "shape", id: shape.id }, x0: pt.x, y0: pt.y };
      return;
    }
    const boxEl = e.target.closest?.("[data-box]");
    if (!boxEl) {
      setSelected(null);
      return;
    }
    const sel = { type: boxEl.dataset.type, id: boxEl.dataset.box };
    setSelected(sel);
    const b = boxes.find((x) => x.id === sel.id);
    if (!b) return;
    dragRef.current = { mode: e.target.dataset.handle ? "resize" : "move", handle: e.target.dataset.handle, sel, start: pt, orig: { ...b } };
    e.preventDefault();
  };

  const onEditMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const pt = ptOf(e);
    if (d.mode === "draw") {
      patchItem(d.sel, {
        x: r4(Math.min(d.x0, pt.x)),
        y: r4(Math.min(d.y0, pt.y)),
        w: r4(Math.abs(pt.x - d.x0)),
        h: r4(Math.abs(pt.y - d.y0)),
      });
      return;
    }
    const dx = pt.x - d.start.x;
    const dy = pt.y - d.start.y;
    const o = d.orig;
    if (d.mode === "move") {
      patchItem(d.sel, { x: r4(clamp(o.x + dx, 0, 1 - o.w)), y: r4(clamp(o.y + dy, 0, 1 - o.h)) });
      return;
    }
    // resize from a corner; the opposite corner stays put
    let x1 = o.x;
    let y1 = o.y;
    let x2 = o.x + o.w;
    let y2 = o.y + o.h;
    if (d.handle.includes("w")) x1 = clamp(o.x + dx, 0, x2 - 0.01);
    if (d.handle.includes("e")) x2 = clamp(o.x + o.w + dx, x1 + 0.01, 1);
    if (d.handle.includes("n")) y1 = clamp(o.y + dy, 0, y2 - 0.01);
    if (d.handle.includes("s")) y2 = clamp(o.y + o.h + dy, y1 + 0.01, 1);
    patchItem(d.sel, { x: r4(x1), y: r4(y1), w: r4(x2 - x1), h: r4(y2 - y1) });
  };

  const onEditUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.mode === "draw") {
      const pt = ptOf(e);
      // a click without a drag still makes a usable box
      if (Math.abs(pt.x - d.x0) < 0.01 || Math.abs(pt.y - d.y0) < 0.01) {
        patchItem(d.sel, { x: r4(clamp(d.x0 - 0.1, 0, 0.8)), y: r4(clamp(d.y0 - 0.05, 0, 0.9)), w: 0.2, h: 0.1 });
      }
      setDrawKind(null);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      if (e.key === "Escape") {
        setDrawKind(null);
        setPickFor(null);
      } else if ((e.key === "Delete" || e.key === "Backspace") && selected?.type === "shape" && !e.repeat) {
        // only while the pointer has been on the preview, so typing elsewhere is safe
        if (editRef.current?.matches(":hover")) {
          e.preventDefault();
          removeShape(selected.id);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /* ---------- save ---------- */

  const save = async () => {
    setSaving(true);
    try {
      const p = projRef.current;
      const savedDoc = await saveVideoProject(slug, {
        title: topic.title || slug,
        topicId: topic.id || slug,
        sourceUrl: p.sourceUrl,
        sourcePath: p.sourcePath,
        sourceName: p.sourceName,
        duration: Number.isFinite(duration) ? round2(duration) : 0,
        offset: p.offset,
        songOn: Boolean(p.songOn),
        oldSong: Boolean(p.oldSong),
        songVolume: vol01(p.songVolume, 1),
        videoVolume: vol01(p.videoVolume, 1),
        showLyrics: p.showLyrics,
        showLogo: p.showLogo,
        overlays: p.overlays,
        shapes: p.shapes,
      });
      onProjectSaved(savedDoc);
      setDirty(false);
      flash("Saved.", "ok");
    } catch (err) {
      flash(
        err?.code === "permission-denied"
          ? "Permission denied saving. Deploy the firestore.rules with the videoProjects block."
          : `Could not save: ${err?.message || err}`,
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const overlayUrls = useMemo(() => proj.overlays.map((o) => o.url).filter(Boolean), [proj.overlays]);
  const projectForExport = useMemo(() => ({ ...savedProject, ...proj }), [savedProject, proj]);
  const missingPics = proj.overlays.filter((o) => !o.url).length;

  return (
    <div className="sa-studio uv-studio">
      <div className="sa-studio-head">
        <button
          type="button"
          className="lb-btn lb-btn-ghost lb-btn-sm"
          onClick={() => {
            if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
            onClose();
          }}
        >
          ← All songs
        </button>
        <div className="sa-studio-title">
          <h3>{topic.title || slug}</h3>
          <span className="sa-muted">
            {slug}
            {proj.sourceName ? ` · ${proj.sourceName}` : ""}
          </span>
        </div>
        {dirty && <span className="sa-pill-inline tone-amber">Unsaved changes</span>}
        <button type="button" className="lb-btn lb-btn-save lb-btn-sm" onClick={save} disabled={saving || !dirty}>
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
      </div>

      {notice && (
        <div className={`sa-notice tone-${notice.tone}`} key={notice.at}>
          {notice.text}
          <button type="button" className="sa-x" onClick={() => setNotice(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}
      {videoError && <div className="sa-banner tone-red">{videoError}</div>}

      {/* The real player: hidden, drawn onto the canvas every frame. Off
          screen rather than display:none so every browser keeps decoding it.
          crossOrigin so a Storage copy can still be drawn and recorded. */}
      {videoSrc && (
        <video
          key={videoSrc}
          ref={videoRef}
          src={videoSrc}
          crossOrigin={localUrl ? undefined : "anonymous"}
          playsInline
          preload="auto"
          className="uv-hidden-video"
          onLoadedMetadata={() => {
            const d = videoRef.current?.duration || 0;
            setDuration(d);
            if (d && Math.abs(d - (projRef.current.duration || 0)) > 0.05) setProjState((prev) => ({ ...prev, duration: round2(d) }));
          }}
          onLoadedData={() => draw(videoRef.current?.currentTime || 0)}
          onPlay={() => {
            setPlaying(true);
            syncSong(true);
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => tickRef.current());
          }}
          onPause={() => {
            setPlaying(false);
            syncSong(true);
          }}
          onEnded={() => syncSong(true)}
          onSeeked={() => {
            const t = videoRef.current?.currentTime || 0;
            setTime(t);
            draw(t);
            syncSong(true);
          }}
          onError={() =>
            setVideoError(
              localUrl
                ? "This browser can’t play that video file. MP4 (H.264) works everywhere; try exporting your video as MP4."
                : "Your saved video couldn’t be loaded. If it plays when opened in a new tab, the bucket’s CORS setting has to list this site."
            )
          }
        />
      )}
      {songUrl && <audio ref={songRef} src={songUrl} preload="auto" onLoadedMetadata={() => syncSong(true)} />}

      <div className="uv-work">
        {/* ---------- left: preview ---------- */}
        <div className="uv-sticky">
          <div className="uv-stage-wrap">
            <canvas ref={canvasRef} className="uv-stage" width={BASE_W} height={BASE_H} aria-label="Video preview" />
            {videoSrc && (
              <div
                ref={editRef}
                className={`uv-edit ${drawKind ? "is-drawing" : ""} ${pickFor ? "is-picking" : ""}`}
                onPointerDown={onEditDown}
                onPointerMove={onEditMove}
                onPointerUp={onEditUp}
                onPointerCancel={onEditUp}
              >
                {boxes.map((b) => {
                  const isSel = selected?.id === b.id;
                  return (
                    <div
                      key={b.id}
                      data-box={b.id}
                      data-type={b.type}
                      className={`uv-box uv-box-${b.type} uv-kind-${b.kind} ${isSel ? "is-selected" : ""}`}
                      style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%` }}
                      title={b.type === "shape" ? `Shape ${b.n} — drag to move` : `Picture ${b.n} — drag to move`}
                    >
                      <span className="uv-box-tag">{b.type === "shape" ? `▭ ${b.n}` : `🖼 ${b.n}`}</span>
                      {isSel &&
                        ["nw", "ne", "sw", "se"].map((h) => <span key={h} data-handle={h} className={`uv-handle uv-handle-${h}`} />)}
                    </div>
                  );
                })}
                {(drawKind || pickFor) && (
                  <div className="uv-edit-hint">
                    {pickFor ? "🎯 Click your video where the colour should come from · Esc to cancel" : "Drag on the video to draw the shape · Esc to cancel"}
                  </div>
                )}
              </div>
            )}
            {!videoSrc && (
              <div className="uv-stage-empty">
                <strong>Upload your video to start</strong>
                <span>Step 1 →</span>
              </div>
            )}
          </div>
          <div className="sa-transport">
            <button type="button" className="lb-btn sa-play" onClick={togglePlay} disabled={!videoSrc}>
              {playing ? "❚❚ Pause" : "▶ Play"}
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => seek(time - 5)} disabled={!videoSrc}>
              −5s
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => seek(time + 5)} disabled={!videoSrc}>
              +5s
            </button>
            <span className="sa-clock">
              {formatTime(time)} / {formatTime(duration, 0)}
            </span>
            <input
              type="range"
              className="sa-seek uv-seek"
              min={0}
              max={duration || 0}
              step={0.01}
              value={Math.min(time, duration || 0)}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Video position"
              disabled={!videoSrc}
            />
            <SyncToggle compact on={proj.showLyrics} onChange={(v) => setProj({ showLyrics: v })} />
          </div>
          {/* where the pictures sit on the timeline */}
          {duration > 0 && proj.overlays.length > 0 && (
            <div className="uv-timeline" aria-hidden="true">
              {proj.overlays.map((o, n) => (
                <button
                  type="button"
                  key={o.id}
                  className={`uv-timeline-clip ${o.url ? "" : "is-empty"}`}
                  style={{
                    left: `${(Math.min(o.from, duration) / duration) * 100}%`,
                    width: `${Math.max(0.6, ((Math.min(o.to, duration) - Math.min(o.from, duration)) / duration) * 100)}%`,
                  }}
                  title={`Picture ${n + 1}: ${formatTime(o.from)} → ${formatTime(o.to)}`}
                  onClick={() => seek(o.from)}
                >
                  {n + 1}
                </button>
              ))}
              <span className="uv-timeline-now" style={{ left: `${(Math.min(time, duration) / duration) * 100}%` }} />
            </div>
          )}
        </div>

        {/* ---------- right: the steps ---------- */}
        <div className="uv-steps">
          <section className="uv-step">
            <header>
              <span className="uv-step-num">1</span>
              <div>
                <h4>Your video</h4>
                <p>MP4 works best. Its sound is kept and used in the finished video.</p>
              </div>
            </header>
            <div className="uv-row">
              <label className={`lb-btn ${proj.sourceUrl || localUrl ? "lb-btn-soft" : ""} uv-upload ${uploadPct ? "is-disabled" : ""}`}>
                {uploadPct
                  ? `Uploading ${Math.round(uploadPct * 100)}%…`
                  : proj.sourceUrl || localUrl
                  ? "Replace video"
                  : "⬆ Upload your video"}
                <input type="file" accept={VIDEO_ACCEPT} className="uv-file-input" onChange={onVideoFile} disabled={Boolean(uploadPct)} />
              </label>
              {proj.sourceName && <code className="uv-file">{proj.sourceName}</code>}
              {duration > 0 && <span className="sa-muted">{formatTime(duration, 0)} long</span>}
            </div>
            {uploadPct > 0 && (
              <div className="sa-progress">
                <div className="sa-progress-bar" style={{ width: `${Math.round(uploadPct * 100)}%` }} />
                <span>Uploading {Math.round(uploadPct * 100)}% — you can keep working while it goes</span>
              </div>
            )}
          </section>

          <section className="uv-step">
            <header>
              <span className="uv-step-num">2</span>
              <div>
                <h4>Sync text (lyrics along the bottom)</h4>
                <p>From this song’s sing-along timings — the same bar as the Lesson video studio. Always drawn on top of shapes and pictures.</p>
              </div>
            </header>
            <div className="uv-row">
              <SyncToggle on={proj.showLyrics} onChange={(v) => setProj({ showLyrics: v })} />
              <span className="sa-muted">
                {proj.showLyrics ? "The lyrics show on this video." : "Off — this video is made without any lyrics."}
              </span>
            </div>
            {!cues.length ? (
              <div className="sa-banner tone-amber">
                This song has no sing-along timings yet, so no lyrics will show. Time it in the{" "}
                <strong>Sing-along studio</strong> first (tap by line or by word), save, then come back.
              </div>
            ) : (
              <>
                <p className="sa-muted">
                  {cues.length} timed line{cues.length === 1 ? "" : "s"}
                  {wordTimedCount > 0
                    ? ` · ${wordTimedCount} of ${parsed.sung.length} lines word-by-word`
                    : " · lines light up by estimate (tap by word in the Sing-along studio for exact words)"}
                  {sync?.reviewed === false ? " · auto-synced, not checked yet" : ""}
                </p>
                <div className="uv-sound">
                  <label className="sa-check">
                    <input type="checkbox" checked={proj.songOn} disabled={!songUrl} onChange={(e) => setProj({ songOn: e.target.checked })} />
                    🎵 Play the topic&apos;s song with my video
                    <span className="sa-muted">
                      {songUrl ? ` (${fileNameOf(songUrl)})` : " — this topic has no song file"}
                    </span>
                  </label>
                  {songChanged && (
                    <div className="sa-banner tone-amber uv-song-changed" style={{ margin: "8px 0" }}>
                      <strong>This topic&apos;s song was changed in the Topic Library</strong> after its lyric timings were made
                      (they were made for <code>{fileNameOf(sync.musicUrl)}</code>).
                      <div className="uv-song-choice" style={{ display: "grid", gap: 4, margin: "8px 0" }}>
                        <label className="sa-check">
                          <input type="radio" name={`uv-song-${slug}`} checked={!proj.oldSong} onChange={() => setProj({ oldSong: false })} />
                          New song from the Topic Library <span className="sa-muted">({fileNameOf(librarySongOf(topic, sync))})</span>
                        </label>
                        <label className="sa-check">
                          <input type="radio" name={`uv-song-${slug}`} checked={Boolean(proj.oldSong)} onChange={() => setProj({ oldSong: true })} />
                          Old song the lyrics were timed to <span className="sa-muted">({fileNameOf(sync.musicUrl)})</span>
                        </label>
                      </div>
                      <span className="sa-muted">
                        With the new song, the lyrics may be out of time if it&apos;s a new recording. To fix that, open the
                        Sing-along studio on this topic: same recording uploaded again — just press Save; a new recording —
                        re-sync it, then Save. Then come back here (Close, then Refresh the list).
                      </span>
                    </div>
                  )}
                  <p className="sa-muted uv-sound-hint">
                    {proj.songOn
                      ? "The song and the lyrics move together with “Song starts at”. Turn your video’s sound down if it has its own music."
                      : "Off: the only music is your video’s own sound, which can’t be moved — “Song starts at” moves the lyrics to match it. Turn this on if your video has no song, or the song in it is in the wrong place."}
                  </p>
                  <div className="uv-row">
                    {proj.songOn && (
                      <label className="uv-vol">
                        Song volume
                        <input type="range" min="0" max="1" step="0.05" value={proj.songVolume} onChange={(e) => setProj({ songVolume: vol01(e.target.value) })} />
                      </label>
                    )}
                    <label className="uv-vol">
                      My video&apos;s sound
                      <input type="range" min="0" max="1" step="0.05" value={proj.videoVolume} onChange={(e) => setProj({ videoVolume: vol01(e.target.value) })} />
                      <span className="sa-muted">{proj.videoVolume === 0 ? "off" : `${Math.round(proj.videoVolume * 100)}%`}</span>
                    </label>
                  </div>
                </div>
                <div className="uv-offset">
                  <span className="uv-offset-label">Song starts at</span>
                  <OffsetInput value={proj.offset} onCommit={(n) => setProj({ offset: n })} />
                  <span className="sa-muted">seconds into your video (minus = cut the song&apos;s intro)</span>
                  {[-1, -0.1, 0.1, 1].map((d) => (
                    <button
                      key={d}
                      type="button"
                      className="lb-btn lb-btn-ghost lb-btn-sm"
                      onClick={() => setProj((prev) => ({ offset: round2(prev.offset + d) }))}
                      title={d < 0 ? (proj.songOn ? "Song and lyrics earlier" : "Lyrics earlier") : proj.songOn ? "Song and lyrics later" : "Lyrics later"}
                    >
                      {d > 0 ? `+${d}` : `−${Math.abs(d)}`}
                    </button>
                  ))}
                </div>
                <div className="uv-row">
                  <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={lyricsStartNow} disabled={!videoSrc || !firstCue}>
                    ⏱ First line starts now ({formatTime(time)})
                  </button>
                  <span className="sa-muted">
                    Play your video and press this the moment “{firstCue ? parsed.lines[firstCue.i] : ""}” is sung.
                  </span>
                </div>
              </>
            )}
            <label className="sa-check">
              <input type="checkbox" checked={proj.showLogo} onChange={(e) => setProj({ showLogo: e.target.checked })} />
              SBL logo in the corner
            </label>
          </section>

          <section className="uv-step uv-step-shapes">
            <header>
              <span className="uv-step-num">3</span>
              <div>
                <h4>Shapes &amp; cover-ups</h4>
                <p>Draw on the video to block out text that’s already in it. Match the background with 🎯, or blur it.</p>
              </div>
            </header>
            <div className="uv-row uv-draw-bar">
              <span className="uv-offset-label">Draw:</span>
              {SHAPE_TYPES.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  className={`lb-btn lb-btn-sm ${drawKind === k.key ? "" : "lb-btn-soft"}`}
                  disabled={!videoSrc}
                  aria-pressed={drawKind === k.key}
                  onClick={() => {
                    setPickFor(null);
                    setDrawKind(drawKind === k.key ? null : k.key);
                  }}
                >
                  {k.icon} {k.label}
                </button>
              ))}
              <label className="uv-color" title="Colour for the next shape you draw">
                <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} aria-label="New shape colour" />
              </label>
            </div>
            {proj.shapes.length === 0 ? (
              <p className="sa-muted">
                Pause on a frame with the text you want gone, press <b>▭ Rectangle</b>, then drag over the text on the
                preview. Press <b>🎯 Pick from video</b> on the shape and click right next to the text to match the
                background. Shapes cover the whole video unless you untick <b>Whole video</b>.
              </p>
            ) : (
              <div className="uv-shapes">
                {proj.shapes.map((shape, n) => (
                  <ShapeRow
                    key={shape.id}
                    shape={shape}
                    n={n + 1}
                    now={time}
                    selected={selected?.id === shape.id}
                    picking={pickFor === shape.id}
                    onSelect={() => selectShape(shape)}
                    onChange={(next) => updateShape(shape.id, next)}
                    onRemove={() => removeShape(shape.id)}
                    onDuplicate={() => duplicateShape(shape)}
                    onPick={() => {
                      setDrawKind(null);
                      setSelected({ type: "shape", id: shape.id });
                      setPickFor(pickFor === shape.id ? null : shape.id);
                    }}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="uv-step">
            <header>
              <span className="uv-step-num">4</span>
              <div>
                <h4>Pictures from one time to another</h4>
                <p>Each picture shows over your video between its From and To times, fading in and out.</p>
              </div>
            </header>
            <div className="uv-row">
              <button type="button" className="lb-btn lb-btn-sm" onClick={addClip} disabled={!videoSrc}>
                + Add picture at {formatTime(time)}
              </button>
              {missingPics > 0 && (
                <span className="sa-pill-inline tone-amber">
                  {missingPics} without a picture yet
                </span>
              )}
            </div>
            {proj.overlays.length === 0 ? (
              <p className="sa-muted">
                Play or scrub to where a picture should appear, then press <b>+ Add picture</b>. Set its end with{" "}
                <b>⟵ now</b> on the To time.
              </p>
            ) : (
              <div className="uv-clips">
                {proj.overlays.map((clip, n) => (
                  <ClipRow
                    key={clip.id}
                    clip={clip}
                    n={n + 1}
                    now={time}
                    duration={duration}
                    lyricAt={lyricAt}
                    selected={selected?.id === clip.id}
                    onSelect={() => selectClip(clip)}
                    onChange={(next) => updateClip(clip.id, next)}
                    onRemove={() => removeClip(clip.id)}
                    onPreview={previewClip}
                    onChoose={(c) => setPicking(c.id)}
                    makeAi={makeAi}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <ExportBox
        topic={topic}
        slug={slug}
        project={projectForExport}
        sync={sync}
        dirty={dirty}
        localFile={localFile}
        renderFrame={renderFrame}
        overlayUrls={overlayUrls}
        onProjectSaved={(d) => {
          setProjState((prev) => ({ ...prev, outputUrl: d.outputUrl, outputPath: d.outputPath, outputSig: d.outputSig }));
          onProjectSaved(d);
        }}
        onTopicChanged={onTopicChanged}
      />

      {picking && (
        <PicturePicker
          pictureMap={pictureMap}
          lines={lines}
          fileKey={fileKey}
          onClose={() => setPicking(null)}
          onPick={({ url, path, prompt }) => {
            const clip = proj.overlays.find((o) => o.id === picking);
            if (clip) updateClip(clip.id, { ...clip, url, path, prompt: clip.prompt || prompt || "" });
            setPicking(null);
          }}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel: pick a song                                                  */
/* ------------------------------------------------------------------ */

const VideoUploadPanel = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topics, setTopics] = useState([]);
  const [syncs, setSyncs] = useState({});
  const [projects, setProjects] = useState({});
  const [pics, setPics] = useState({});
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, s, p, im] = await Promise.all([
        getAllLibraryTopics(),
        getAllLyricSyncs(),
        getAllVideoProjects().catch((err) => {
          if (err?.code === "permission-denied") throw err;
          return {};
        }),
        getAllLyricImages().catch(() => ({})),
      ]);
      setTopics(t);
      setSyncs(s);
      setProjects(p);
      setPics(im);
    } catch (err) {
      setError(
        err?.code === "permission-denied"
          ? "Permission denied reading videoProjects. Deploy the updated firestore.rules (it adds the videoProjects block)."
          : `Could not load: ${err?.message || err}`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !topics.length && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toolKey = useMemo(() => makeToolKey(topics), [topics]);

  const rows = useMemo(
    () =>
      topics
        .map((topic) => {
          const slug = toolKey(topic);
          const sync =
            syncs[slug] ||
            (slug === legacyKey(topic.slug || topic.id) ? syncs[exactKey(topic.slug || topic.id)] : null) ||
            null;
          const t = { ...topic, id: topic.id || topic.slug, slug };
          const project = projects[slug] || null;
          const timed = Boolean(sync && (sync.cues || []).length);
          const hasLyrics = timed || parseLyrics(topic.lyrics).sung.length > 0;
          return { topic: t, sync, project, timed, hasLyrics };
        })
        .filter((r) => r.hasLyrics || r.project)
        .sort((a, b) => {
          const rank = (r) => (r.project ? 0 : r.timed ? 1 : 2);
          return rank(a) - rank(b) || String(a.topic.title || a.topic.slug).localeCompare(String(b.topic.title || b.topic.slug));
        }),
    [topics, syncs, projects, toolKey]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyMine && !r.project) return false;
      if (!q) return true;
      return String(r.topic.title || "").toLowerCase().includes(q) || r.topic.slug.includes(q);
    });
  }, [rows, search, onlyMine]);

  const active = activeSlug ? rows.find((r) => r.topic.slug === activeSlug) : null;

  const openStudio = (slug) => {
    setActiveSlug(slug);
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  return (
    <section className="lb-card sa-panel uv-panel" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step sa-step uv-head-step">📹</span>
        <div>
          <h2>Video upload studio</h2>
          <p className="lb-hint">
            Upload your own video and add the song’s lyrics along the bottom (lighting up word by word) and AI
            pictures from one time to another. Separate from the Lesson video studio — nothing there changes.
          </p>
        </div>
        <div className="sa-head-actions">
          {open && !active && (
            <button type="button" className="lb-btn lb-btn-soft" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
          <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          {error && <div className="sa-banner tone-red">{error}</div>}

          {active ? (
            <Studio
              key={active.topic.slug}
              topic={active.topic}
              sync={active.sync}
              project={active.project}
              pictureMap={lyricImageUrls(pics[active.topic.slug])}
              onClose={() => setActiveSlug(null)}
              onProjectSaved={(saved) =>
                setProjects((prev) => ({ ...prev, [active.topic.slug]: { ...(prev[active.topic.slug] || {}), ...saved } }))
              }
              onTopicChanged={(patch) =>
                setTopics((prev) => prev.map((t) => ((t.id || t.slug) === active.topic.id ? { ...t, ...patch } : t)))
              }
            />
          ) : (
            <>
              <div className="sa-controls-row">
                <input className="sa-search" placeholder="Search songs…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <label className="sa-check">
                  <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
                  Only songs with a video project
                </label>
              </div>
              {loading && !topics.length ? (
                <p className="sa-muted">Loading songs…</p>
              ) : visible.length === 0 ? (
                <p className="sa-empty-row">No songs match.</p>
              ) : (
                <div className="sa-table-wrap">
                  <table className="sa-table sa-list">
                    <thead>
                      <tr>
                        <th>Song</th>
                        <th>Lyrics timing</th>
                        <th>My video</th>
                        <th aria-label="Open" />
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => (
                        <tr key={r.topic.slug}>
                          <td>
                            <div className="sa-topic-title">{r.topic.title || r.topic.slug}</div>
                            <div className="sa-muted sa-topic-slug">{r.topic.slug}</div>
                          </td>
                          <td>
                            {r.timed ? (
                              <span className="sa-pill-inline tone-blue">timed</span>
                            ) : (
                              <span className="sa-pill-inline tone-grey">not timed — no lyrics bar</span>
                            )}
                          </td>
                          <td>
                            {r.project?.outputUrl ? (
                              <span className="sa-pill-inline tone-green">MP4 made</span>
                            ) : r.project?.sourceUrl ? (
                              <span className="sa-pill-inline tone-amber">video uploaded</span>
                            ) : (
                              <span className="sa-muted">—</span>
                            )}
                          </td>
                          <td className="sa-row-actions">
                            <button type="button" className="lb-btn lb-btn-sm" onClick={() => openStudio(r.topic.slug)}>
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default VideoUploadPanel;
